import React, { type FC } from "react";
import { useIntl } from "react-intl";

import {
  Flex,
  FlexItem,
  GRID_VARIANT_VERTICAL_16,
  Grid,
  GridCell,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_ITALIC_LIGHT,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
} from "mailup-web-components";

import { evalCondition } from "mailup-react-helpers";

import { isNullOrEmpty } from "Helpers/strings";

import RenderWhen from "Components/RenderWhen";

import type { SearchResult } from "Pages/RecipientsList/model";

import EditStatus from "Pages/RecipientsList/components/aside/EditStatus";
import EmailSubscriptionDetails from "Pages/RecipientsList/components/aside/EmailSubscriptionDetails";
import SmsSubscriptionDetails from "Pages/RecipientsList/components/aside/SmsSubscriptionDetails";
import ItemStatus from "Pages/RecipientsList/components/results/ItemStatus";

import IconBanRegular from "Components/icons/IconBanRegular";
import IconEnvelope from "Components/icons/IconEnvelope";
import IconFlagSwallowtail from "Components/icons/IconFlagSwallowtail";
import IconMobile from "Components/icons/IconMobile";
import IconTrackingStatus from "../../../../icons/iconTrackingStatus.svg";

import IconPaperPlane from "Components/icons/IconPaperPlane";
import {
  canChangeEmailSubscriptionStatus,
  canChangeSmsSubscriptionStatus,
  getOptoutText,
} from "Pages/RecipientsList/recoil/services/recipient";
import { useRecoilValue } from "recoil";
import { panelEditRecipientState } from "Pages/RecipientsList/recoil/state/recipient";
import EditStatusTracking from "./EditStatusTracking";
import TrackinkStatusDetails from "./TrackinkStatusDetails";

interface BoxFixedFieldsProps {
  item: SearchResult;
}

const BoxFixedFields: FC<BoxFixedFieldsProps> = ({ item }) => {
  const intl = useIntl();

  const canChangeEmailStatus = canChangeEmailSubscriptionStatus(item);
  const canChangeSmsStatus = canChangeSmsSubscriptionStatus(item);

  const hasAdvRecMgmt: boolean = evalCondition("co:ADVRECIPIENTMGMT");
  const { tracking } = useRecoilValue(panelEditRecipientState);

  return (
    <Grid variantVertical={GRID_VARIANT_VERTICAL_16} cols={12}>
      <GridCell span={5}>
        <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
          <IconEnvelope width="18px" height="18px" />
          {intl.formatMessage({ id: "Load.RecipientsList.ColumnEmail" })}
        </Text>
      </GridCell>
      <GridCell span={7}>
        {isNullOrEmpty(item.Email) ? (
          <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
            {intl.formatMessage({ id: "Load.RecipientsList.EmptyData" })}
          </Text>
        ) : (
          <Text variant={TEXT_VARIANT_LIGHT} wordBreak="break-all">
            {item.Email}
          </Text>
        )}
      </GridCell>

      <GridCell span={5}>
        <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
          <IconFlagSwallowtail width="16px" height="18px" />
          {intl.formatMessage({
            id: "Load.RecipientsList.ColumnEmailSubscriptionStatus",
          })}
        </Text>
      </GridCell>
      <GridCell span={7}>
        {canChangeEmailStatus ? (
          <EditStatus
            status={item.EmailSubscriptionStatus}
            channel="EMAIL"
            icon={
              <EmailSubscriptionDetails
                recipient={item}
                status={item.EmailSubscriptionStatus}
              />
            }
          />
        ) : (
          <ItemStatus id={item.Id} status={item.EmailSubscriptionStatus} />
        )}
      </GridCell>

      {item.EmailSubscriptionStatus === "UNSUBSCRIBED" &&
        item.ReasonEmail !== undefined && (
          <>
            <GridCell span={5}>
              <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
                <IconBanRegular width="17px" height="17px" />
                {intl.formatMessage({
                  id: "Load.RecipientsList.UnsubscriptionReason",
                })}
              </Text>
            </GridCell>
            <GridCell span={7}>
              <Text variant={TEXT_VARIANT_LIGHT} wordBreak="break-all">
                {getOptoutText(intl, item.ReasonEmail)}
              </Text>
            </GridCell>
          </>
        )}

      <RenderWhen
        condition={hasAdvRecMgmt && item.EmailSubscriptionStatus === "PENDING"}
      >
        <GridCell span={5}>
          <Flex gap="16px">
            <FlexItem
              alignSelf="flex-start"
              flex="16px 0 0"
              padding="4px 0 0 0"
            >
              <IconPaperPlane width="15px" height="15px" />
            </FlexItem>
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: "Load.RecipientsList.PendingRequestDate",
              })}
            </Text>
          </Flex>
        </GridCell>
        <GridCell span={7}>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT} wordBreak="break-all">
            <RenderWhen condition={item.EmailPendingDateExt !== undefined}>
              {intl.formatMessage(
                { id: "Load.RecipientsList.PendingRequestSentOn" },
                {
                  DATE: item.EmailPendingDateExt,
                },
              )}
            </RenderWhen>
            <RenderWhen condition={item.EmailPendingDateExt === undefined}>
              {intl.formatMessage({
                id: "Load.RecipientsList.PendingRequestNotSent",
              })}
            </RenderWhen>
          </Text>
        </GridCell>
      </RenderWhen>

      <GridCell span={5}>
        <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
          <IconMobile width="14px" height="18px" />
          {intl.formatMessage({ id: "Load.RecipientsList.ColumnMobile" })}
        </Text>
      </GridCell>
      <GridCell span={7}>
        {isNullOrEmpty(item.Mobile) ? (
          <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
            {intl.formatMessage({ id: "Load.RecipientsList.EmptyData" })}
          </Text>
        ) : (
          <Text variant={TEXT_VARIANT_LIGHT} wordBreak="break-all">
            {item.Mobile}
          </Text>
        )}
      </GridCell>

      <GridCell span={5}>
        <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
          <IconFlagSwallowtail width="16px" height="18px" />
          {intl.formatMessage({
            id: "Load.RecipientsList.ColumnSMSSubscriptionStatus",
          })}
        </Text>
      </GridCell>
      <GridCell span={7}>
        {canChangeSmsStatus ? (
          <EditStatus
            status={item.SMSSubscriptionStatus}
            channel="SMS"
            icon={
              <SmsSubscriptionDetails
                recipient={item}
                status={item.SMSSubscriptionStatus}
              />
            }
          />
        ) : (
          <ItemStatus id={item.Id} status={item.SMSSubscriptionStatus} />
        )}
      </GridCell>

      {item.SMSSubscriptionStatus === "UNSUBSCRIBED" &&
        item.ReasonSms !== undefined && (
          <>
            <GridCell span={5}>
              <Text variant={TEXT_VARIANT_BOLD} alignContent gap="16px">
                <IconBanRegular width="17px" height="17px" />
                {intl.formatMessage({
                  id: "Load.RecipientsList.UnsubscriptionReason",
                })}
              </Text>
            </GridCell>
            <GridCell span={7}>
              <Text variant={TEXT_VARIANT_LIGHT} wordBreak="break-all">
                {getOptoutText(intl, item.ReasonSms)}
              </Text>
            </GridCell>
          </>
        )}

      <GridCell span={5}>
        <Text variant={TEXT_VARIANT_BOLD} alignContent gap="12px">
          <img src={IconTrackingStatus} alt="" />
          {intl.formatMessage({
            id: "Load.RecipientsList.TrackingStatus",
          })}
        </Text>
      </GridCell>
      <GridCell span={7}>
        <Flex alignItems="center" justifyContent="space-between" width="160px">
          <EditStatusTracking
            id={item.Id}
            tracking={tracking ?? false}
            icon={<TrackinkStatusDetails tracking={tracking ?? false} />}
          />
        </Flex>
      </GridCell>
    </Grid>
  );
};

export default BoxFixedFields;
